import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPinDialog({ open, onOpenChange, initialPhone = "" }) {
  const [phone, setPhone] = useState(initialPhone);
  const [step, setStep] = useState(1); // 1=send, 2=reset
  const [code, setCode] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(1); setCode(""); setNewPin(""); setConfirmPin("");
  };

  const sendCode = async () => {
    if (!phone || phone.length < 6) return toast.error("Enter your phone number");
    setBusy(true);
    try {
      await api.post("/auth/forgot-pin", { phone });
      toast.success("If your account is registered, a reset code has been sent to your email.");
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reset code");
    } finally { setBusy(false); }
  };

  const submitReset = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code from your email");
    if (!/^\d{4}$/.test(newPin)) return toast.error("PIN must be 4 digits");
    if (newPin !== confirmPin) return toast.error("PINs do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-pin", { phone, code, new_pin: newPin, new_pin_confirm: confirmPin });
      toast.success("PIN reset successful. You can now sign in with your new PIN.");
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset PIN");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="forgot-pin-dialog" className="bg-[#170929] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Forgot your PIN?</DialogTitle>
          <DialogDescription className="text-slate-400">
            {step === 1
              ? "Enter your registered phone number. We'll email you a 6-digit code to reset your PIN."
              : "Enter the code from your email and choose a new 4-digit PIN."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Phone number</Label>
              <Input
                data-testid="forgot-pin-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="velo-input mt-2 h-12 font-mono"
                placeholder="Enter your phone number"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">6-digit code</Label>
              <Input
                data-testid="forgot-pin-code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="velo-input mt-2 h-12 text-center font-mono text-lg tracking-[0.3em]"
                placeholder="123456"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">New PIN</Label>
              <Input
                data-testid="forgot-pin-new"
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                className="velo-input mt-2 h-12 text-center font-mono text-lg tracking-[0.4em]"
                placeholder="••••"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Confirm PIN</Label>
              <Input
                data-testid="forgot-pin-confirm"
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                className="velo-input mt-2 h-12 text-center font-mono text-lg tracking-[0.4em]"
                placeholder="••••"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 2 && (
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5"
            >
              Back
            </Button>
          )}
          {step === 1 ? (
            <Button
              data-testid="forgot-pin-send"
              onClick={sendCode}
              disabled={busy}
              className="rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium"
            >
              Send reset code
            </Button>
          ) : (
            <Button
              data-testid="forgot-pin-submit"
              onClick={submitReset}
              disabled={busy}
              className="rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium"
            >
              Reset PIN
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
